// Import React modules
import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { ref, uploadBytesResumable, getStorage } from "firebase/storage";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Player } from "@lottiefiles/react-lottie-player";
import { db, storage } from "../firebase";
// import { auth, db, app } from "../firebase";
import { RootState } from "./../redux/store";
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/buttonStyles.css";
import UAParser from "ua-parser-js";
import axios from "axios";
import { StorageReference } from "firebase/storage";

interface RegenerateAudioRecorderProps {
  mode: "generate" | "dictate";
  onAudioUrl: (url: string, fullFilename: string, isSetCurrentRecording: boolean) => void;
  onRecordingClick: () => void;
  userId: string;
  recordingId: string;
  sessionNotesUpload: string;
  noteTemplate: string;
  templateID: string;
  templateCategory: string;
  templateIsPreset: boolean;
  newTranscript: string;
  setNewTranscript: React.Dispatch<React.SetStateAction<string>>;
  setIsTranscribing: React.Dispatch<React.SetStateAction<boolean>>;
  isTranscribing: boolean;
}

export default function RegenerateAudioRecorder({
  mode,
  onAudioUrl,
  onRecordingClick,
  userId,
  recordingId,
  sessionNotesUpload,
  noteTemplate,
  templateID,
  templateCategory,
  templateIsPreset,
  newTranscript,
  setNewTranscript,
  setIsTranscribing,
  isTranscribing,
}: RegenerateAudioRecorderProps) {
  const user = useSelector((state: RootState) => state.user);
  const [recording, setRecording] = useState<boolean>(false);
  const [paused, setPaused] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [timerDisplay, setTimerDisplay] = useState<string>("00:00:00");
  const playerRef = useRef<Player>(null);
  const audioChunks = useRef<BlobPart[]>([]);
  const url = useRef<string | null>(null);
  const recordingCounter = useRef(0);
  const [isUploading, setIsUploading] = useState(false);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const startTime = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
//   const [isTranscribing, setIsTranscribing] = useState(false);
//   const [transcript, setTranscript] = useState<string>("");

  // Timer
  useEffect(() => {
    const currentStartTime = Date.now();
    startTime.current = currentStartTime; // Use a ref to store start time

    if (recording && !paused) {
      intervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime.current; // Use ref here
        setTimer(Math.floor(elapsedTime / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [recording, paused]);

  // Timer conversion for display
  useEffect(() => {
    const hours = Math.floor(timer / 3600);
    const minutes = Math.floor((timer % 3600) / 60);
    const seconds = timer % 60;
    setTimerDisplay(
      `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    );
  }, [timer]);

  //   Request microphone permission
  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Microphone permission granted, you can proceed with recording
      return stream;
    } catch (error) {
      // Microphone permission denied or an error occurred
      console.error("Microphone permission denied:", error);
      throw error;
    }
  };

  const handleRecordingClick = () => {
    // Call the general click handler
    onRecordingClick();

    // Then call either startRecording or stopRecording based on the recording state
    if (recording) {
      stopRecording();
    } else {
      if (noteTemplate === "") {
        alert("Please select a note template before starting recording.");
      } else {
        startRecording();
      }
    }
  };

  const startRecording = async () => {
    try {
      // console.trace("Start recording");
      if (
        mediaRecorder.current &&
        mediaRecorder.current.state === "recording"
      ) {
        return; // Prevent starting a new recording if already recording
      }
      recordingCounter.current = 0;
      const stream = await requestMicrophonePermission();

      // console.log("Starting recording..");
      // Detect the browser using the user agent
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isChrome =
        /chrome/i.test(navigator.userAgent) &&
        navigator.userAgent.includes("Chrome");

      // Set MIME type based on browser
      const mimeType = isSafari
        ? "audio/mp4"
        : isChrome
        ? "audio/webm"
        : "audio/mp3";
      const options = {
        mimeType: mimeType,
      };
      mediaRecorder.current = new MediaRecorder(stream, options);
      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.start();
      setRecording(true);
      setNewTranscript("");
    } catch (err) {
      console.error("Microphone permission denied:", err);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.pause();
      if (playerRef.current) {
        playerRef.current.pause();
      }
    }
    setPaused(true);
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "paused") {
      mediaRecorder.current.resume();
      if (playerRef.current) {
        playerRef.current.play();
      }
    }
    setPaused(false);
  };

  const stopRecording = async () => {
    // console.log("Stop recording");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (mediaRecorder.current) {
      const stream = await mediaRecorder.current.stream;
      stream.getTracks().forEach((track) => track.stop());
      mediaRecorder.current.stop();
      if (playerRef.current) {
        playerRef.current.stop();
      }
mediaRecorder.current.onstop = async () => {
    recordingCounter.current += 1;
    // Need to execute Media Recorder onStop logic only on the second recording when using Safari
    if (!isSafari || (isSafari && recordingCounter.current === 2)) {
      const audioBlob = new Blob(audioChunks.current, {
        type: "audio/wav",
      });
      url.current = URL.createObjectURL(audioBlob);
      const filename = `${user.uid}_${new Date().toISOString()}`;
      const fileExtension = determineFileExtension();
      const fullFilename = `${filename}${fileExtension}`;
      onAudioUrl(url.current, fullFilename, false);
      const uploadResult = await uploadAudio(url.current, timer, fullFilename);
      if (uploadResult) {
        const { storageRef, transcriptId } = uploadResult;
        setIsTranscribing(true);
        if (storageRef) {
        //   console.log("User ID: ", user.uid);
        //   console.log("Recording ID: ", recordingId);
        //   console.log("transcriptId: ", transcriptId);
        //   console.log("storageRef: ", storageRef);
  
          // Ensure the audio has completed uploading
          const uploadTask = uploadBytesResumable(storageRef, audioBlob); // Use audioBlob here
          uploadTask.on(
            "state_changed",
            (snapshot) => {},
            (error) => {
              console.error("Upload failed", error);
              setUploadStatus("Upload failed");
              setIsTranscribing(false);
            },
            async () => {
              // Upload completed successfully
            //   console.log("Upload completed successfully");
              await transcribeAudio(user.uid, recordingId, transcriptId, storageRef);
            }
          );
        } else {
          console.error("Storage reference is undefined");
          setIsTranscribing(false);
        }
      } else {
        console.error("Upload failed");
        setIsTranscribing(false);
      }
    }
  };
    }
    setRecording(false);
    setTimer(0);
  };

  function getOperatingSystem(result: UAParser.IResult): string {
    return `${result.os.name} ${result.os.version}`;
  }

  function getDevice(result: UAParser.IResult): string {
    // Device type might not be defined for all user agents, e.g., desktop browsers
    const deviceType = result.device.type ? result.device.type : "Desktop";
    const deviceModel = result.device.model ? result.device.model : "N/A";
    return `${deviceType} - ${deviceModel}`;
  }

  function getBrowser(result: UAParser.IResult): string {
    return `${result.browser.name} ${result.browser.version}`;
  }

  async function getIpAddress(): Promise<string> {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip;
    } catch (error) {
      console.error("Error fetching IP address:", error);
      return "N/A";
    }
  }

  function formatDuration(duration: number): string {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    return [
      hours.toString().padStart(2, "0"),
      minutes.toString().padStart(2, "0"),
      seconds.toString().padStart(2, "0"),
    ].join(":");
  }

  const uploadAudio = async (
    url: string,
    duration: number,
    fullFilename: string
  ) => {
    if (isUploading) {
      return; // Prevent multiple uploads at the same time
    }
    setIsUploading(true);

    const parser = new UAParser();
    const result = parser.getResult();
    const operatingSystem = getOperatingSystem(result);
    const device = getDevice(result);
    const browser = getBrowser(result);
    const ipAddress = await getIpAddress();

    const response = await fetch(url);
    const blob = await response.blob();
    const formattedDuration = formatDuration(duration);

    const userDocRef = doc(db, "users", user.uid);
    const recordingsDocRef = doc(
      userDocRef,
      "recordings",
      recordingId
    );
    const transcriptVersionsCollectionRef = collection(
      recordingsDocRef,
      "transcriptVersions"
    );
    const transcriptData = {
      timestamp: serverTimestamp(),
      progress: "Preparing recording",
      sessionNotes: sessionNotesUpload,
      templateID: templateID,
      templateCategory: templateCategory,
      templateIsPreset: templateIsPreset,
      operatingSystem: operatingSystem,
      device: device,
      browser: browser,
      filename: fullFilename,
      duration: formattedDuration,
      ipAddress: ipAddress,
    };

    try {
      // console.log("Attempting audio upload to cloud...");
      // Add the recording data to the "recordings" collection
      let storageRef;
      const docRef = await addDoc(
        transcriptVersionsCollectionRef,
        transcriptData
      );
      const transcriptId = docRef.id;
      // console.log("Recording ID generated: ", recordingId);
      const transcriptDocRef = doc(
        transcriptVersionsCollectionRef,
        transcriptId
      );
      // Create audio file name
      const filename = `${
        user.uid
      }_${new Date().toISOString()}_${recordingId}_${transcriptId}`;
      // console.log("Filename:", filename);
      const fileExtension = determineFileExtension();
      const fullFilename = `${filename}${fileExtension}`;
      // const storage = getStorage(app, "gs://motics-consultations-staging");
      storageRef = ref(
        storage,
        `transcriptAudioFiles/${user.uid}/${recordingId}/${fullFilename}`
      );

      setUploadStatus("Metadata updated, starting file upload...");

      // Upload the audio file to Firebase Storage
      // console.trace("Uploading to storage");
      const uploadTask = uploadBytesResumable(storageRef, blob);

      uploadTask.on(
        "state_changed",
        (snapshot) => {},
        (error) => {
          console.error("Upload failed", error);
          setUploadStatus("Upload failed");
          const progressData = {
            progress: "Upload failed",
          };
          updateDoc(transcriptDocRef, progressData)
            .then(() => {
              // console.log("Progress field updated successfully");
            })
            .catch((error) => {
              console.error("Error updating progress field: ", error);
            });
        },
        () => {
          // console.log("Uploaded to Firebase Cloud Storage");
          setUploadStatus(
            "Upload successful! Your summary will be emailed to you shortly."
          );
          const progressData = {
            progress: "Processing recording",
          };
          updateDoc(transcriptDocRef, progressData)
            .then(() => {
              // console.log("Progress field updated successfully");
            })
            .catch((error) => {
              console.error("Error updating progress field: ", error);
            });
          setTimeout(() => {
            setUploadStatus("");
          }, 1500);
        }
      );
      return { storageRef, transcriptId };
    } catch (error) {
      console.error("Error updating Firestore or uploading file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const transcribeAudio = async (
    userId: string,
    recordingId: string,
    transcriptId: string,
    storageRef: StorageReference
  ) => {
    try {
      const response = await axios.post(
        "https://europe-west2-motics-consultations.cloudfunctions.net/generate-transcript",
        {
          userId,
          recordingId,
          transcriptId,
          storageRef,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.data && response.data.transcript) {
        // console.log("Transcript received:", response.data.transcript);
        setNewTranscript(response.data.transcript);
      } else {
        console.warn("No transcript found in the response data");
      }
  
      // You can add any additional async operations here if needed
      // For example:
      // await someAsyncOperation(response.data);
  
    } catch (error) {
      console.error("Error in transcribeAudio:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios error details:", {
          message: error.message,
          code: error.code,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          } : 'No response',
        });
      }
    } finally {
    //   console.log("Transcription process completed");
      setIsTranscribing(false);
    }
  };

  function determineFileExtension() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isChrome =
      /chrome/i.test(navigator.userAgent) &&
      /google inc/i.test(navigator.vendor);
    return isSafari ? ".m4a" : isChrome ? ".webm" : ".mp3";
  }

  return (
    <div className="box-sizing-default audio-recorder-container">
      <div style={{ display: "flex" }}>
        <button
          className={recording ? "button-record-red" : "button-record"}
          onClick={handleRecordingClick}
          disabled={isTranscribing} // Disable button while transcribing
        >
          {isTranscribing
            ? "Transcribing..."
            : recording
            ? "Stop Recording"
            : "Start Recording"}
        </button>
        {recording && !isTranscribing && (
          <button
            className={paused ? "button-classic-green" : "button-record"}
            onClick={paused ? resumeRecording : pauseRecording}
          >
            {paused ? "Resume Recording" : "Pause Recording"}
          </button>
        )}
      </div>
      {!recording && newTranscript.trim() !== '' && (
        <div className="alert-text" style={{ textAlign: "center", marginTop: "0.5rem" }}>
            Start recording would replace the new transcript below.
        </div>
      )}
      {recording && (
        <>
          <div
            className="alert-text"
            style={{ textAlign: "center", marginTop: "0.5rem" }}
          >
            Please keep this tab open while recording.
          </div>
          <p>{timerDisplay}</p>
        </>
      )}
      {/* {isTranscribing && (
        <div className="loading-animation">
          Loading...
        </div>
      )} */}
      {newTranscript.trim() !== '' && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            width: "100%",
            marginTop: "1rem",
          }}
        >
          <p>New transcript:</p>
          <p>{newTranscript}</p>
        </div>
      )}
    </div>
  );
}
